import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["selectedRows", "toggleSelectAll", "totalRows"];
let _ = t => t,
  _t;
import I18n from 'I18n';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { Table } from 'foundations-components/transitional/components/Table';
import styledWithTheme, { useTheme } from 'foundations-theming/utils';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const TableContext = /*#__PURE__*/createContext({
  selectedRows: [],
  totalRows: 0,
  toggleRowSelection: () => {},
  toggleSelectAll: () => {},
  deselectAllRows: () => {}
});
export const TableProvider = ({
  children,
  ids
}) => {
  const [selectedRows, setSelectedRows] = useState([]);
  const toggleSelectAll = useCallback(() => {
    const selectAll = selectedRows.length === 0;
    setSelectedRows(selectAll ? ids : []);
  }, [ids, selectedRows.length]);
  const deselectAllRows = useCallback(() => {
    setSelectedRows([]);
  }, []);
  const toggleRowSelection = useCallback(id => {
    setSelectedRows(rows => rows.includes(id) ? rows.filter(value => value !== id) : [...rows, id]);
  }, []);
  const contextState = useMemo(() => ({
    selectedRows,
    totalRows: ids.length,
    deselectAllRows,
    toggleSelectAll,
    toggleRowSelection
  }), [selectedRows, ids.length, deselectAllRows, toggleSelectAll, toggleRowSelection]);
  return /*#__PURE__*/_jsx(TableContext.Provider, {
    value: contextState,
    children: children
  });
};
const CHECKBOX_COLUMN_WIDTH = 60;
const StyledTableHeader = styledWithTheme.tr(_t || (_t = _`
  background-color: ${0};
  position: relative;
  th:not(.fixed-header) {
    text-transform: none !important;
    visibility: ${0};
  }
`), ({
  theme
}) => theme.color['fill-surface-raised'], ({
  $showActions
}) => $showActions ? 'hidden' : 'visible');
const HeaderActions = styledWithTheme.td(({
  theme
}) => ({
  background: 'none',
  blockSize: '100%',
  borderWidth: `${theme.borderWidth['0']} !important`,
  padding: theme.space['0'],
  position: 'absolute'
}));
const TableHeader = ({
  renderHeader,
  renderHeaderActions
}) => {
  const _useContext = useContext(TableContext),
    {
      selectedRows,
      toggleSelectAll,
      totalRows
    } = _useContext,
    rest = _objectWithoutPropertiesLoose(_useContext, _excluded);
  const showActions = selectedRows.length > 0;
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsxs(StyledTableHeader, {
      $showActions: showActions ? 1 : 0,
      children: [renderHeaderActions &&
      /*#__PURE__*/
      // @bend-expect trellis-tokenized
      _jsx("th", {
        className: "fixed-header",
        children: /*#__PURE__*/_jsx(Checkbox, {
          "aria-label": I18n.text('learningCenter.table.checkboxAllTip'),
          checked: selectedRows.length === totalRows,
          indeterminate: selectedRows.length > 0,
          onChange: toggleSelectAll
        })
      }), /*#__PURE__*/_jsx(HeaderActions, {
        children: /*#__PURE__*/_jsx(TFlex, {
          align: "center",
          blockSize: "100%",
          paddingInlineStart: "600",
          children: showActions && renderHeaderActions && renderHeaderActions(Object.assign({
            selectedRows,
            toggleSelectAll,
            totalRows
          }, rest))
        })
      }), renderHeader()]
    })
  );
};
const AcademyTableRow = ({
  rowId,
  row,
  renderRow,
  hasActions
}) => {
  const {
    selectedRows,
    toggleRowSelection
  } = useContext(TableContext);
  const theme = useTheme();
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [hasActions && /*#__PURE__*/_jsx("td", {
      children: /*#__PURE__*/_jsx(Checkbox, {
        "aria-label": I18n.text('learningCenter.table.checkboxTip'),
        checked: selectedRows.includes(rowId),
        onChange: () => toggleRowSelection(rowId)
      })
    }), /*#__PURE__*/_jsx("td", {
      style: {
        padding: theme.space['0']
      }
    }), renderRow(row)]
  });
};
const AcademyTable = ({
  rows,
  renderHeader,
  renderHeaderActions,
  renderRow
}) => {
  const ids = Array.from(rows.keys());
  const hasActions = !!renderHeaderActions;
  const theme = useTheme();
  return /*#__PURE__*/_jsx(TableProvider, {
    ids: ids,
    children: /*#__PURE__*/_jsxs(Table, {
      condensed: true,
      children: [hasActions && /*#__PURE__*/_jsx("colgroup", {
        children: /*#__PURE__*/_jsx("col", {
          style: {
            width: CHECKBOX_COLUMN_WIDTH
          }
        })
      }), /*#__PURE__*/_jsx("thead", {
        children: /*#__PURE__*/_jsx(TableHeader, {
          renderHeader: renderHeader,
          renderHeaderActions: renderHeaderActions,
          rows: rows
        })
      }), /*#__PURE__*/_jsx("tbody", {
        children: ids.map(id => /*#__PURE__*/_jsx("tr", {
          "data-test-id": `row_${id}`,
          style: {
            background: theme.color['fill-surface-default']
          },
          children: /*#__PURE__*/_jsx(AcademyTableRow, {
            hasActions: hasActions,
            renderRow: renderRow,
            row: rows.get(id),
            rowId: id
          })
        }, id))
      })]
    })
  });
};
export default AcademyTable;